import { create } from 'zustand'
import type { GraphEdge, GraphNode, GraphNodeData } from '@/types/graph'
import type {
  CalistoGuidedWorkflowStep,
  CalistoSessionContextSummary,
} from '@/services/api/calisto'

export interface ContentBlock {
  type: 'text' | 'tool_use' | 'tool_result'
  text?: string
  id?: string
  tool_use_id?: string
  name?: string
  input?: Record<string, unknown>
  content?: unknown
  is_error?: boolean
}

export interface CalistoMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  isStreaming?: boolean
  contentBlocks?: ContentBlock[]
}

export interface CalistoDesiredContext {
  targetDeploymentId?: string | null
  activeMetaRecetteId?: string | null
  source?: string | null
}

export interface CalistoObserveTarget {
  orgId: string
  deploymentId: string
  metaRecetteId: string
  applyRunId?: string | null
  testRunId?: string | null
  updatedAt: number
}

export interface CalistoDraftPrompt {
  id: number
  text: string
}

export type CanvasAction = {
  id: string
  type:
    | 'create_node'
    | 'update_node'
    | 'delete_node'
    | 'create_edge'
    | 'delete_edge'
  status: 'pending' | 'approved' | 'rejected'
  nodeId?: string
  edgeId?: string
  nodeData?: Partial<GraphNodeData>
  node?: GraphNode
  edge?: GraphEdge
}

function normalizeDesiredContext(
  context: CalistoDesiredContext | null | undefined,
): CalistoDesiredContext | null {
  if (!context) {
    return null
  }

  const targetDeploymentId = context.targetDeploymentId?.trim() || null
  const activeMetaRecetteId = context.activeMetaRecetteId?.trim() || null
  const source = context.source?.trim() || null

  if (!targetDeploymentId && !activeMetaRecetteId) {
    return null
  }

  return {
    targetDeploymentId,
    activeMetaRecetteId,
    source,
  }
}

function hasSameDesiredContextProjection(
  left: CalistoDesiredContext | null | undefined,
  right: CalistoDesiredContext | null | undefined,
) {
  return (
    (left?.targetDeploymentId ?? null) === (right?.targetDeploymentId ?? null) &&
    (left?.activeMetaRecetteId ?? null) === (right?.activeMetaRecetteId ?? null)
  )
}

function normalizeObserveTarget(
  target: CalistoObserveTarget | null | undefined,
): CalistoObserveTarget | null {
  if (!target) {
    return null
  }

  const orgId = target.orgId?.trim() || ''
  const deploymentId = target.deploymentId?.trim() || ''
  const metaRecetteId = target.metaRecetteId?.trim() || ''
  const applyRunId = target.applyRunId?.trim() || null
  const testRunId = target.testRunId?.trim() || null
  const updatedAt = Number.isFinite(target.updatedAt) ? target.updatedAt : Date.now()

  if (!orgId || !deploymentId || !metaRecetteId) {
    return null
  }

  return {
    orgId,
    deploymentId,
    metaRecetteId,
    applyRunId,
    testRunId,
    updatedAt,
  }
}

interface CalistoStore {
  currentSessionId: string | null
  roomId: string | null
  messages: CalistoMessage[]
  historyLoading: boolean
  historyLoaded: boolean
  sessionActive: boolean
  chatOpen: boolean
  isProcessing: boolean
  lastResponse: string | null
  error: string | null
  canvasActions: CanvasAction[]
  desiredContext: CalistoDesiredContext | null
  observeTarget: CalistoObserveTarget | null
  contextSummary: CalistoSessionContextSummary | null
  guidedWorkflow: CalistoGuidedWorkflowStep[]
  guidedConvergeLoading: boolean
  guidedConvergePreparing: boolean
  guidedConvergeApplying: boolean
  guidedConvergeError: string | null
  guidedConvergeStatusMessage: string | null
  draftPrompt: CalistoDraftPrompt | null
  addMessage: (message: CalistoMessage) => void
  setMessages: (messages: CalistoMessage[]) => void
  setHistoryLoading: (loading: boolean) => void
  setHistoryLoaded: (loaded: boolean) => void
  upsertMessage: (message: CalistoMessage) => void
  appendAssistantDelta: (id: string, delta: string) => void
  clearMessages: () => void
  setCurrentSessionId: (sessionId: string | null) => void
  setRoomId: (roomId: string) => void
  setSessionActive: (active: boolean) => void
  setChatOpen: (open: boolean) => void
  setIsProcessing: (processing: boolean) => void
  setLastResponse: (response: string | null) => void
  setError: (error: string | null) => void
  setDesiredContext: (context: CalistoDesiredContext | null) => void
  clearDesiredContext: () => void
  setObserveTarget: (target: CalistoObserveTarget | null) => void
  clearObserveTarget: () => void
  setContextSummary: (summary: CalistoSessionContextSummary | null) => void
  setGuidedWorkflow: (workflow: CalistoGuidedWorkflowStep[]) => void
  setGuidedConvergeLoading: (loading: boolean) => void
  setGuidedConvergePreparing: (preparing: boolean) => void
  setGuidedConvergeApplying: (applying: boolean) => void
  setGuidedConvergeError: (error: string | null) => void
  setGuidedConvergeStatusMessage: (message: string | null) => void
  setDraftPrompt: (text: string) => void
  consumeDraftPrompt: (id: number) => void
  upsertCanvasAction: (action: CanvasAction) => void
  updateCanvasActionStatus: (
    actionId: string,
    status: CanvasAction['status'],
  ) => void
  clearCanvasActions: () => void
}

export const useCalistoStore = create<CalistoStore>((set) => ({
  currentSessionId: null,
  roomId: null,
  messages: [],
  historyLoading: false,
  historyLoaded: false,
  sessionActive: false,
  chatOpen: false,
  isProcessing: false,
  lastResponse: null,
  error: null,
  canvasActions: [],
  desiredContext: null,
  observeTarget: null,
  contextSummary: null,
  guidedWorkflow: [],
  guidedConvergeLoading: false,
  guidedConvergePreparing: false,
  guidedConvergeApplying: false,
  guidedConvergeError: null,
  guidedConvergeStatusMessage: null,
  draftPrompt: null,
  addMessage: (message) =>
    set((state) => ({
      messages: [...state.messages, message],
    })),
  setMessages: (messages) => set({ messages }),
  setHistoryLoading: (historyLoading) => set({ historyLoading }),
  setHistoryLoaded: (historyLoaded) => set({ historyLoaded }),
  upsertMessage: (message) =>
    set((state) => {
      const existingIndex = state.messages.findIndex((item) => item.id === message.id)
      if (existingIndex === -1) {
        return { messages: [...state.messages, message] }
      }

      const nextMessages = [...state.messages]
      nextMessages[existingIndex] = message
      return { messages: nextMessages }
    }),
  appendAssistantDelta: (id, delta) =>
    set((state) => {
      const existing = state.messages.find((item) => item.id === id)
      if (!existing) {
        return {
          messages: [
            ...state.messages,
            {
              id,
              role: 'assistant',
              content: delta,
              timestamp: new Date(),
              isStreaming: true,
            },
          ],
        }
      }

      return {
        messages: state.messages.map((item) =>
          item.id === id
            ? {
                ...item,
                content: `${item.content}${delta}`,
                isStreaming: true,
              }
            : item,
        ),
      }
    }),
  clearMessages: () =>
    set({
      messages: [],
      lastResponse: null,
      error: null,
      historyLoading: false,
      historyLoaded: false,
    }),
  setCurrentSessionId: (currentSessionId) => set({ currentSessionId }),
  setRoomId: (roomId) => set({ roomId }),
  setSessionActive: (active) => set({ sessionActive: active }),
  setChatOpen: (open) => set({ chatOpen: open }),
  setIsProcessing: (processing) => set({ isProcessing: processing }),
  setLastResponse: (response) => set({ lastResponse: response }),
  setError: (error) => set({ error }),
  setDesiredContext: (desiredContext) =>
    set((state) => {
      const nextDesiredContext = normalizeDesiredContext(desiredContext)
      const shouldResetGuidedState = !hasSameDesiredContextProjection(
        state.desiredContext,
        nextDesiredContext,
      )

      return {
        desiredContext: nextDesiredContext,
        ...(shouldResetGuidedState
          ? {
              contextSummary: null,
              guidedWorkflow: [],
              guidedConvergeLoading: false,
              guidedConvergePreparing: false,
              guidedConvergeApplying: false,
              guidedConvergeError: null,
              guidedConvergeStatusMessage: null,
              observeTarget: null,
            }
          : {}),
      }
    }),
  clearDesiredContext: () =>
    set((state) => {
      if (!state.desiredContext) {
        return {}
      }

      return {
        desiredContext: null,
        contextSummary: null,
        guidedWorkflow: [],
        guidedConvergeLoading: false,
        guidedConvergePreparing: false,
        guidedConvergeApplying: false,
        guidedConvergeError: null,
        guidedConvergeStatusMessage: null,
        observeTarget: null,
      }
    }),
  setObserveTarget: (observeTarget) =>
    set({ observeTarget: normalizeObserveTarget(observeTarget) }),
  clearObserveTarget: () => set({ observeTarget: null }),
  setContextSummary: (contextSummary) => set({ contextSummary }),
  setGuidedWorkflow: (guidedWorkflow) => set({ guidedWorkflow }),
  setGuidedConvergeLoading: (guidedConvergeLoading) => set({ guidedConvergeLoading }),
  setGuidedConvergePreparing: (guidedConvergePreparing) =>
    set({ guidedConvergePreparing }),
  setGuidedConvergeApplying: (guidedConvergeApplying) =>
    set({ guidedConvergeApplying }),
  setGuidedConvergeError: (guidedConvergeError) => set({ guidedConvergeError }),
  setGuidedConvergeStatusMessage: (guidedConvergeStatusMessage) =>
    set({ guidedConvergeStatusMessage }),
  setDraftPrompt: (text) =>
    set({ draftPrompt: { id: Date.now(), text } }),
  consumeDraftPrompt: (id) =>
    set((state) => (state.draftPrompt?.id === id ? { draftPrompt: null } : {})),
  upsertCanvasAction: (action) =>
    set((state) => {
      const existingIndex = state.canvasActions.findIndex((item) => item.id === action.id)
      if (existingIndex === -1) {
        return { canvasActions: [...state.canvasActions, action] }
      }

      const nextActions = [...state.canvasActions]
      nextActions[existingIndex] = {
        ...nextActions[existingIndex],
        ...action,
      }

      return { canvasActions: nextActions }
    }),
  updateCanvasActionStatus: (actionId, status) =>
    set((state) => ({
      canvasActions: state.canvasActions.map((action) =>
        action.id === actionId ? { ...action, status } : action,
      ),
    })),
  clearCanvasActions: () => set({ canvasActions: [] }),
}))
